import { DatePicker, TimePicker } from "antd";
import { Col, Form, Input, Row, Select, Tag } from "antd-v5";
import { flatMap, uniq } from "lodash";
import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LabelValueNumber } from "types/common";
import { disablePastDates } from "utils/dateUtils";
import { parseSelectOptions } from "utils/formUtils";
import { intersection, isEmpty, isString, map } from "utils/lodashUtils";

export const REPEAT_SELECT_OPTIONS = [
  { label: "Every week", value: "everyWeek" },
  { label: "Specific week", value: "specificWeek" },
  { label: "Every month", value: "everyMonth" },
  { label: "Specific month", value: "specificMonth" },
];

export const Days = [
  { label: "S", value: 1 },
  { label: "M", value: 2 },
  { label: "T", value: 3 },
  { label: "W", value: 4 },
  { label: "T", value: 5 },
  { label: "F", value: 6 },
  { label: "S", value: 7 },
];
export const daysValues = Days.map((entry) => entry.value);
export const defaultTime = moment("2013-02-08 00:00:00");

const SPECIFIC_MONTH_OPTIONS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

enum CustomRepeatOptions {
  HOURS = "HOURS",
  DAYS = "DAYS",
}

const EVERY_OPTIONS = [
  { label: "Hours", value: CustomRepeatOptions.HOURS },
  { label: "Days", value: CustomRepeatOptions.DAYS },
];

export const FIRST_WEEK_DAYS_OPTIONS = map(
  ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th"],
  (opt) => ({
    label: opt,
    value: Number(opt[0]),
  }),
);
export const LAST_WEEK_DAYS_OPTIONS = map(
  ["22nd", "23rd", "24th", "25th", "26th", "27th", "28th"],
  (opt) => ({
    label: opt,
    value: Number(`${opt[0]}${opt[1]}`),
  }),
);
const WEEK_NUMBER_OPTIONS = [
  { label: "Week 1", value: 1 },
  { label: "Week 2", value: 2 },
  { label: "Week 3", value: 3 },
  { label: "Week 4", value: 4 },
];

export const FIRST_LAST_WEEK_OPTIONS = [
  { label: "First week", value: "firstWeek" },
  { label: "Last week", value: "lastWeek" },
];

interface ConfigCron {
  time?: Moment | undefined;
  daysOfTheWeek?: number[] | undefined;
  weekNumber?: number[] | undefined;
  dates?: number[] | undefined;
  months?: string[] | undefined;
  repeatOption?: string;
  isFirstLastWeek?: string;
}

const defaultValues = {
  repeatOption: "everyWeek",
  isFirstLastWeek: "firstWeek",
  selectedTime: moment("2013-02-08 00:00:00"),
  selectedDays: [2],
  selectedDates: [1],
  selectedWeekNumber: [1],
  selectedMonths: ["Jan"],
  lastSelectedDate: [28],
  fixedRateUnit: CustomRepeatOptions.DAYS,
  fixedRateValue: 15,
};

export type CustomRate = {
  fixedRateUnit: CustomRepeatOptions;
  fixedRateValue: number;
  type: "FIXED_RATE";
  startAt: Moment;
};

const getConfigData = (cron: string): ConfigCron => {
  const data = getConfigFromAdvancedCronString(cron);

  return data;
};

export const DEFAULT_CRON_VALUES: { [key: string]: string } = {
  everyWeek: "0 0 9 ? * 2",
  specificWeek: "0 0 9 ? * 2#1",
  everyMonth: "0 0 9 1 * ?",
  specificMonth: "0 0 9 1 Jan ?",
};

const ACAdvancedCron = ({
  onChange,
  onCustomChange,
  value: cron,
  customSupport = false,
  layout = "horizontal",
  customRepeat,
}: {
  onChange?: (cronString: string | undefined) => void;
  onCustomChange?: (cronString: CustomRate | undefined) => void;
  value?: string;
  customSupport?: boolean;
  layout?: "vertical" | "horizontal";
  customRepeat?: CustomRate;
}) => {
  const isVerticalLayout = layout === "vertical";
  //initial config for handling on edit cron
  const initialConfig = useMemo(
    () => (cron ? getConfigData(cron) : {}),
    [cron],
  );
  //states for cron fields
  const [repeatOption, setRepeatOption] = useState<string>(
    initialConfig.repeatOption ?? defaultValues.repeatOption,
  );
  const [isFirstLastWeek, setisFirstLastWeek] = useState<string>("firstWeek");
  const [selectedTime, setSelectedTime] = useState<Moment>(
    initialConfig.time ?? defaultValues.selectedTime,
  );
  const [selectedDays, setSelectedDays] = useState<number[] | undefined>(
    initialConfig.daysOfTheWeek ?? defaultValues.selectedDays,
  );
  const [selectedDates, setSelectedDates] = useState<number[] | undefined>(
    initialConfig.dates ?? defaultValues.selectedDates,
  );
  const [selectedWeekNumber, setSelectedWeekNumber] = useState<
    number[] | undefined
  >(initialConfig.weekNumber ?? defaultValues.selectedWeekNumber);
  const [selectedMonths, setSelectedMonths] = useState<string[] | undefined>(
    initialConfig.months ?? defaultValues.selectedMonths,
  );
  const [fixedRateUnit, setFixedRateUnit] = useState(
    customRepeat?.fixedRateUnit ?? defaultValues.fixedRateUnit,
  );
  const [fixedRateValue, setFixedRateValue] = useState(
    customRepeat?.fixedRateValue ?? defaultValues.fixedRateValue,
  );

  useEffect(() => {
    //set states on edit cron
    if (cron) {
      const config = getConfigData(cron);
      setRepeatOption(config.repeatOption ?? defaultValues.repeatOption);
      setSelectedTime(config.time ?? defaultValues.selectedTime);
      setSelectedDays(config.daysOfTheWeek ?? defaultValues.selectedDays);
      setSelectedDates(config.dates ?? defaultValues.selectedDates);
      setSelectedWeekNumber(
        config.weekNumber ?? defaultValues.selectedWeekNumber,
      );
      setSelectedMonths(config.months ?? defaultValues.selectedMonths);
      setisFirstLastWeek(
        config.isFirstLastWeek ?? defaultValues.isFirstLastWeek,
      );
    } else if (customRepeat) {
      setRepeatOption("custom");
      setSelectedTime(
        moment(customRepeat.startAt) ?? defaultValues.selectedTime,
      );
      setFixedRateUnit(
        customRepeat.fixedRateUnit ?? defaultValues.fixedRateUnit,
      );
      setFixedRateValue(
        customRepeat.fixedRateValue ?? defaultValues.fixedRateValue,
      );
    }
  }, [cron, customRepeat]);

  const getCronString = useCallback(
    ({
      time,
      daysOfTheWeek,
      weekNumber,
      dates,
      months,
      rate,
      rateUnit,
      repeatOption,
    }) => {
      const cronString = getAdvancedCronStringFromConfig({
        time,
        daysOfTheWeek,
        weekNumber,
        dates,
        months,
        repeatOption,
      });
      if (onChange) {
        onChange(cronString);
      }
      if (onCustomChange) {
        onCustomChange(
          repeatOption === "custom"
            ? {
                type: "FIXED_RATE",
                startAt: time,
                fixedRateUnit: rateUnit,
                fixedRateValue: rate,
              }
            : undefined,
        );
      }
    },
    [onChange, onCustomChange],
  );

  const handleDaySelection = (day: number, checked: boolean) => {
    const daySelection = selectedDays ? [...selectedDays] : [];
    if (checked === true) {
      daySelection.push(day);
    } else {
      const spliceIndex = daySelection.indexOf(day);
      daySelection.splice(spliceIndex, 1);
    }
    setSelectedDays(daySelection);
    getCronString({
      time: selectedTime,
      daysOfTheWeek: daySelection,
      weekNumber: selectedWeekNumber,
      dates: selectedDates,
      months: selectedMonths,
      rate: fixedRateValue,
      rateUnit: fixedRateUnit,
      repeatOption,
    });
  };

  const handleTimeSelection = (time: Moment, option?: string) => {
    setSelectedTime(time);
    getCronString({
      time,
      daysOfTheWeek: selectedDays,
      weekNumber: selectedWeekNumber,
      dates: selectedDates,
      months: selectedMonths,
      rate: fixedRateValue,
      rateUnit: fixedRateUnit,
      repeatOption: option || repeatOption,
    });
  };

  const handleWeekNumberSelection = (weekNumber: number[]) => {
    setSelectedWeekNumber(weekNumber);
    getCronString({
      time: selectedTime,
      daysOfTheWeek: selectedDays,
      weekNumber,
      dates: selectedDates,
      months: selectedMonths,
      rate: fixedRateValue,
      rateUnit: fixedRateUnit,
      repeatOption,
    });
  };

  const handleDateSelection = (day: number, checked: boolean) => {
    const dateSelection = selectedDates ? [...selectedDates] : [];
    if (checked === true) {
      dateSelection.push(day);
    } else {
      const spliceIndex = dateSelection.indexOf(day);
      dateSelection.splice(spliceIndex, 1);
    }
    setSelectedDates(dateSelection);
    getCronString({
      time: selectedTime,
      daysOfTheWeek: selectedDays,
      weekNumber: selectedWeekNumber,
      dates: dateSelection,
      months: selectedMonths,
      rate: fixedRateValue,
      rateUnit: fixedRateUnit,
      repeatOption,
    });
  };

  const handleMonthSelection = (months: string[]) => {
    setSelectedMonths(months);
    getCronString({
      time: selectedTime,
      daysOfTheWeek: selectedDays,
      weekNumber: selectedWeekNumber,
      dates: selectedDates,
      months,
      rate: fixedRateValue,
      rateUnit: fixedRateUnit,
      repeatOption,
    });
  };

  const handleFixedRateUnitChange = (rateUnit: CustomRepeatOptions) => {
    setFixedRateUnit(rateUnit);
    getCronString({
      time: selectedTime,
      daysOfTheWeek: selectedDays,
      weekNumber: selectedWeekNumber,
      dates: selectedDates,
      months: selectedMonths,
      rateUnit,
      rate: fixedRateValue,
      repeatOption,
    });
  };

  const handleFixedRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFixedRateValue(+e.target.value);
    getCronString({
      time: selectedTime,
      daysOfTheWeek: selectedDays,
      weekNumber: selectedWeekNumber,
      dates: selectedDates,
      months: selectedMonths,
      rate: +e.target.value,
      rateUnit: fixedRateUnit,
      repeatOption,
    });
  };

  const getCheckableTags = (
    array: LabelValueNumber[],
    onChange: (value: number, checked: boolean) => void,
    selectedArr: number[] | undefined,
  ) => {
    const isMonth =
      ["specificMonth", "everyMonth"].indexOf(repeatOption) !== -1;
    return (
      <Form.Item label={isMonth ? "Date" : "Days"}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {array.map(({ label, value }) => (
            <Tag.CheckableTag
              key={`${label}_${value}`}
              className={`runbook-checkable-tags`}
              style={{
                padding: isMonth ? "4px 3px" : "4px 10px",
              }}
              checked={(selectedArr || []).indexOf(value) > -1}
              onChange={(checked) => onChange(value, checked)}
            >
              {label}
            </Tag.CheckableTag>
          ))}
        </div>
      </Form.Item>
    );
  };

  const FirstLastWeekSelect = (
    <Form.Item label={"Week"}>
      <Select
        options={FIRST_LAST_WEEK_OPTIONS}
        style={{ width: "100%" }}
        placeholder="Select first/last week"
        value={isFirstLastWeek}
        onChange={(week) => {
          setisFirstLastWeek(week);
          getCronString({
            time: selectedTime,
            daysOfTheWeek: selectedDays,
            weekNumber: selectedWeekNumber,
            dates:
              week === "firstWeek"
                ? defaultValues.selectedDates
                : defaultValues.lastSelectedDate,
            months: selectedMonths,
            rate: fixedRateValue,
            rateUnit: fixedRateUnit,
            repeatOption,
          });
        }}
      />
    </Form.Item>
  );

  const handleRepeatSeletion = (option: string) => {
    setRepeatOption(option);
    if (option === "custom") {
      const defaultTime = moment().startOf("day").add(1, "days");
      handleTimeSelection(defaultTime, option);
    } else if (onChange) {
      onChange(DEFAULT_CRON_VALUES[option]);
    }
  };

  const selectOptions = useMemo(() => {
    return [
      ...REPEAT_SELECT_OPTIONS,
      ...(customSupport ? [{ label: "Custom", value: "custom" }] : []),
    ];
  }, [customSupport]);

  const colSpan = isVerticalLayout ? 24 : 12;
  const isCustom = repeatOption === "custom";

  return (
    <Input.Group>
      <Row gutter={[16, 16]}>
        <Col span={colSpan}>
          <Select
            options={selectOptions}
            style={{ width: "100%" }}
            value={repeatOption}
            onChange={handleRepeatSeletion}
            placeholder="Select options"
          />
        </Col>
        <Col span={colSpan} />
        {repeatOption === "specificWeek" && (
          <Col span={colSpan}>
            <Form.Item label={"Week"}>
              <Select
                options={WEEK_NUMBER_OPTIONS}
                style={{ width: "100%" }}
                placeholder="Select week number"
                value={selectedWeekNumber}
                onChange={handleWeekNumberSelection}
                mode="multiple"
              />
            </Form.Item>
          </Col>
        )}
        {"specificMonth" === repeatOption && (
          <Col span={colSpan}>
            <Form.Item label={"Month"}>
              <Select
                options={parseSelectOptions(SPECIFIC_MONTH_OPTIONS)}
                style={{ width: "100%" }}
                placeholder="Select month"
                onChange={handleMonthSelection}
                value={selectedMonths}
                mode="multiple"
              />
            </Form.Item>
          </Col>
        )}
        {["everyMonth", "specificMonth"].includes(repeatOption) && (
          <Col span={colSpan}>{FirstLastWeekSelect}</Col>
        )}
        {["specificWeek"].includes(repeatOption) && (
          <Col span={colSpan}>
            {getCheckableTags(Days, handleDaySelection, selectedDays)}
          </Col>
        )}
        {["everyMonth", "specificMonth"].includes(repeatOption) && (
          <Col span={colSpan}>
            {getCheckableTags(
              map(
                isFirstLastWeek === "firstWeek"
                  ? FIRST_WEEK_DAYS_OPTIONS
                  : LAST_WEEK_DAYS_OPTIONS,
              ),
              handleDateSelection,
              selectedDates,
            )}
          </Col>
        )}
        {["everyWeek"].includes(repeatOption) && (
          <Col span={colSpan}>
            {getCheckableTags(Days, handleDaySelection, selectedDays)}
          </Col>
        )}
        {isCustom && (
          <>
            <Col span={24}>
              <Form.Item name="every" label={"Every"}>
                <Input
                  type="number"
                  onChange={handleFixedRateChange}
                  value={fixedRateValue}
                  defaultValue={fixedRateValue}
                  min={1}
                  max={fixedRateUnit === "HOURS" ? 24 : 365}
                  addonAfter={
                    <Select
                      style={{ width: 100 }}
                      options={parseSelectOptions(EVERY_OPTIONS)}
                      placeholder="Select option"
                      onChange={handleFixedRateUnitChange}
                      value={fixedRateUnit}
                    />
                  }
                />
              </Form.Item>
            </Col>
          </>
        )}
        <Col span={isVerticalLayout ? 24 : 8}>
          <Form.Item
            required={isCustom}
            label={`${isCustom ? "Start Date/" : ""}Time`}
          >
            {isCustom ? (
              <DatePicker
                showTime
                use12Hours
                format="YYYY-MM-DD h:mm a"
                allowClear={false}
                value={selectedTime}
                disabledDate={disablePastDates}
                onSelect={handleTimeSelection}
              />
            ) : (
              <TimePicker
                popupClassName="custom-time-picker"
                value={selectedTime}
                use12Hours
                allowClear={false}
                showNow={false}
                format="h:mm a"
                onSelect={handleTimeSelection}
              />
            )}
          </Form.Item>
        </Col>
      </Row>
    </Input.Group>
  );
};

export default ACAdvancedCron;

//convert config to cron string
export const getConfigFromAdvancedCronString = (cronString: string) => {
  if (!isString(cronString)) {
    return {};
  }

  const cronAllowedLength = 6;
  const cronConfig = cronString.split(" ");
  if (!Array.isArray(cronConfig) || cronConfig.length !== cronAllowedLength) {
    return {};
  }

  const [second, minute, hour, dayOfTheMonth, month, daysOfTheWeekString] =
    cronConfig;
  let daysOfTheWeek: number[] | string = daysOfTheWeekString;
  let repeatOption;
  let isFirstLastWeek: string = "firstWeek";

  if (dayOfTheMonth === "?") {
    if (daysOfTheWeekString.includes("#")) {
      repeatOption = "specificWeek";
    } else {
      repeatOption = "everyWeek";
    }
  }

  if (daysOfTheWeekString === "?") {
    if (month === "*") {
      repeatOption = "everyMonth";
    } else {
      repeatOption = "specificMonth";
    }
  }

  if (
    !isString(daysOfTheWeek) ||
    daysOfTheWeek === "?" ||
    daysOfTheWeek === ""
  ) {
    daysOfTheWeek = [];
  } else if (daysOfTheWeek === "*") {
    daysOfTheWeek = [1, 2, 3, 4, 5, 6, 7];
  } else {
    daysOfTheWeek = uniq(
      daysOfTheWeek.split(",").map((day) => {
        if (day.includes("#")) {
          return Number(day.split("#")[0]);
        }
        return Number(day);
      }),
    );
  }

  const time = moment(`${hour}:${minute}:${second}`, "HH:mm:ss");

  let weekNumber: number[] | undefined;
  let dates: number[] | undefined;
  let months: string[] | undefined;

  if (cronString.includes("#")) {
    weekNumber = uniq(
      daysOfTheWeekString.split(",").map((i) => Number(i.split("#")[1])),
    );
  }

  if (dayOfTheMonth !== "?") {
    dates = dayOfTheMonth.split(",").map((date) => Number(date));
  }

  if (month !== "*") {
    months = !isEmpty(month) ? month.split(",") : [];
  }

  if (!isEmpty(dates)) {
    if (
      intersection(
        dates,
        map(FIRST_WEEK_DAYS_OPTIONS, (option) => option.value),
      ).length > 0
    ) {
      isFirstLastWeek = "firstWeek";
    } else {
      isFirstLastWeek = "lastWeek";
    }
  }
  return {
    time,
    daysOfTheWeek,
    weekNumber,
    dates,
    months,
    repeatOption,
    isFirstLastWeek,
  };
};

//convert cron to config
export const getAdvancedCronStringFromConfig = ({
  time,
  daysOfTheWeek,
  weekNumber,
  dates,
  months,
  repeatOption,
}: {
  time: Moment;
  daysOfTheWeek?: number[];
  weekNumber?: number[];
  dates?: number[];
  months?: string[];
  repeatOption?: string;
}) => {
  if (!moment.isMoment(time)) {
    return;
  }
  let cronString = "";
  let second;
  let minute;
  let hour;
  let dayOfTheMonth;
  let month;
  let daysOfTheWeekString;
  switch (repeatOption) {
    case "everyWeek":
      second = 0;
      minute = time.get("minute");
      hour = time.get("hour");
      dayOfTheMonth = "?";
      month = "*";
      daysOfTheWeekString = daysOfTheWeek?.join(",");
      cronString = `${second} ${minute} ${hour} ${dayOfTheMonth} ${month} ${daysOfTheWeekString}`;
      break;
    case "specificWeek":
      second = 0;
      minute = time.get("minute");
      hour = time.get("hour");
      dayOfTheMonth = "?";
      month = "*";
      daysOfTheWeekString = uniq(
        flatMap(weekNumber, (weekNumber) =>
          map(daysOfTheWeek, (day) => `${day}#${weekNumber}`),
        ),
      );
      cronString = `${second} ${minute} ${hour} ${dayOfTheMonth} ${month} ${daysOfTheWeekString}`;
      break;
    case "everyMonth":
      second = 0;
      minute = time.get("minute");
      hour = time.get("hour");
      month = "*";
      daysOfTheWeekString = "?";
      dayOfTheMonth = dates?.join();
      cronString = `${second} ${minute} ${hour} ${dayOfTheMonth} ${month} ${daysOfTheWeekString}`;
      break;
    case "specificMonth":
      second = 0;
      minute = time.get("minute");
      hour = time.get("hour");
      month = months?.join();
      daysOfTheWeekString = "?";
      dayOfTheMonth = dates?.join();
      cronString = `${second} ${minute} ${hour} ${dayOfTheMonth} ${month} ${daysOfTheWeekString}`;
      break;
  }
  return cronString;
};
